/**
 * Builds the srcdoc for a widget frame: our runtime, then the model's HTML.
 *
 * Ordering is styles → content → script so something meaningful paints before
 * the script runs.
 *
 * The runtime is the ONLY channel the widget has to the outside. It exposes a
 * deliberately tiny surface (`window.host`) — resize is automatic, and the
 * three verbs map exactly to the messages the parent will validate. Anything
 * a widget tries outside this either fails (opaque origin) or is ignored.
 *
 * Nothing here interpolates a token, cookie, or session value. The only
 * dynamic input is the model's HTML and a list of tableIds it may request.
 */

// Runtime injected into every frame. Kept small and dependency-free — it ships
// on every widget.
const RUNTIME = `
<script>
(function () {
  var send = function (msg) {
    try { parent.postMessage(msg, "*"); } catch (e) { /* opaque origin: nothing to do */ }
  };

  // Report content height so the parent can size the frame. A nested scrollbar
  // inside a chat message is the thing this exists to prevent.
  var lastHeight = 0;
  var report = function () {
    var body = document.body;
    var html = document.documentElement;
    var height = Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      html ? html.scrollHeight : 0,
      html ? html.offsetHeight : 0
    );
    if (height && Math.abs(height - lastHeight) > 1) {
      lastHeight = height;
      send({ type: "resize", height: height });
    }
  };

  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(report).observe(document.documentElement);
  }
  window.addEventListener("load", report);
  setTimeout(report, 0);
  setTimeout(report, 120);

  var pending = {};
  window.addEventListener("message", function (event) {
    var data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "data" && pending[data.requestId]) {
      pending[data.requestId](data.rows || []);
      delete pending[data.requestId];
    }
  });

  var seq = 0;
  window.host = {
    // Ask the chat a follow-up, as if the user typed it.
    sendPrompt: function (text) { send({ type: "sendPrompt", text: String(text) }); },
    // Parent validates the scheme and confirms before navigating.
    openLink: function (url) { send({ type: "openLink", url: String(url) }); },
    // Data by REFERENCE. The widget never receives a retyped dataset.
    requestData: function (tableId, callback, page) {
      var requestId = "r" + (++seq);
      pending[requestId] = callback;
      send({ type: "requestData", tableId: String(tableId), page: page || 1, requestId: requestId });
    },
    resize: report
  };
})();
<\/script>`;

// Baseline styling so a widget inherits the host's look without being told.
// Values are injected by the parent from the runtime tokens; the widget never
// hardcodes a colour.
const baseStyles = tokens => `
<style>
  :root {
    --cp-ink: ${tokens.ink};
    --cp-ink-2: ${tokens.ink2};
    --cp-ink-3: ${tokens.ink3};
    --cp-surface: ${tokens.surface};
    --cp-bg: ${tokens.bg};
    --cp-border: ${tokens.border};
    --cp-blue: ${tokens.blue};
    --cp-series-1: ${tokens.series1};
    --cp-series-2: ${tokens.series2};
    --cp-series-3: ${tokens.series3};
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    /* No scrollbar inside the frame — the parent sizes it to fit. */
    overflow: hidden;
    background: transparent;
    color: var(--cp-ink);
    font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 13px;
    line-height: 1.5;
  }
  body { padding: 12px; }
  button {
    font: inherit;
    color: var(--cp-ink);
    background: var(--cp-surface);
    border: 1px solid var(--cp-border);
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
  }
  input[type="range"] { width: 100%; }
</style>`;

/**
 * `tokens` are plain colour strings the parent already resolved from CSS
 * custom properties — see cssVar. Passing colours is safe; passing anything
 * identifying is not, and nothing here does.
 */
export const buildWidgetDocument = (html, tokens) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8">${baseStyles(tokens)}</head>` +
  `<body>${html || ""}${RUNTIME}</body></html>`;

export default buildWidgetDocument;
